import axios from "../utils/axiosConfig";
import { handleApiError } from "../utils/errorHandler";
import config from "../config/config";

const BASE_URL = `${config.apiUrl}/api/order`;

export const orderService = {
  // Obtener todas las órdenes activas
  getAllOrders: async () => {
    try {
      const response = await axios.get(`${BASE_URL}/active`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Obtener una orden por su ID
  getOrderById: async (orderId) => {
    try {
      const response = await axios.get(`${BASE_URL}/${orderId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Obtener órdenes por estado
  getOrdersByStatus: async (status) => {
    try {
      const response = await axios.get(`${BASE_URL}/status/${status}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Actualizar una orden existente
  updateOrder: async (orderId, orderData) => {
    try {
      const response = await axios.put(`${BASE_URL}/${orderId}`, orderData);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Actualizar el estado de una orden
  updateOrderStatus: async (orderId, status) => {
    try {
      const response = await axios.patch(`${BASE_URL}/${orderId}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Verificar el pago de una orden
  verifyPayment: async (orderId, paymentMethod) => {
    try {
      const response = await axios.post(`${BASE_URL}/${orderId}/payment`, {
        method: paymentMethod,
        status: "Pagado",
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Solicitar el pago de una orden
  requestPayment: async (orderId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/${orderId}/request-payment`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async listOrders() {
    try {
      const response = await axios.get(`${BASE_URL}/list`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Órdenes obtenidas exitosamente",
      };
    } catch (error) {
      const apiError = handleApiError(error);
      return {
        success: false,
        data: null,
        message: apiError.message,
      };
    }
  },

  // Función auxiliar para agrupar órdenes por mesa
  groupOrdersByTable(orders) {
    return orders.reduce((grouped, order) => {
      const key = order.mesaId;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(order);
      return grouped;
    }, {});
  },

  // Función para formatear las órdenes al formato esperado por el componente
  formatOrders(orders) {
    return orders.map((order) => ({
      _id: order._id,
      id: order._id,
      date: new Date(order.date).toLocaleString(),
      products: order.items.map((item) => ({
        name: item.name,
        quantity: item.quantity || 1,
        total: item.price * (item.quantity || 1),
        selectedAddon: item.selectedAddon, // Incluir el addOn en el formateo
      })),
      table: order.descriptionTable,
      status: order.status,
      payment: order.payment,
    }));
  },

  verifyPayment: async (orderId, tipoPago) => {
    try {
      console.log("Enviando petición de pago:", { orderId, tipoPago }); // Log de petición

      const response = await axios.post(`${BASE_URL}/verify`, {
        orderId,
        tipoPago,
      });

      console.log("Respuesta del servidor:", response.data); // Log de respuesta

      return {
        success: true,
        data: response.data,
        message: response.data.message || "Pago procesado exitosamente",
      };
    } catch (error) {
      console.error("Error en servicio de pago:", error); // Log de error
      const apiError = handleApiError(error);
      return {
        success: false,
        data: null,
        message: apiError.message,
      };
    }
  },

  createOrder: async (orderData) => {
    try {
      // Verificar explícitamente el valor antes de la transformación
      console.log(
        "OrderService - isArqueoActive original:",
        orderData.isArqueoActive
      );
      console.log(
        "OrderService - tipo de isArqueoActive:",
        typeof orderData.isArqueoActive
      );

      // Asegurarnos de que isArqueoActive sea un booleano correcto
      // No modificamos el objeto original para evitar efectos secundarios
      const orderToSend = {
        ...orderData,
        isArqueoActive: orderData.isArqueoActive === true,
      };

      // Verificar después de la transformación
      console.log("OrderService - orderToSend final:", orderToSend);
      console.log(
        "OrderService - isArqueoActive final:",
        orderToSend.isArqueoActive
      );
      console.log(
        "OrderService - tipo final de isArqueoActive:",
        typeof orderToSend.isArqueoActive
      );

      const response = await axios.post(`${BASE_URL}/orderResto`, orderToSend);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: false,
        data: null,
        message: response.data.message,
      };
    } catch (error) {
      console.log(error);
      throw handleApiError(error);
    }
  },

  createPrintManifest: async (orderData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/pendingManifests`,
        orderData
      );
      return {
        success: true,
        data: response.data.data,
        message:
          response.data.message ||
          "Manifiesto de impresión creado exitosamente",
      };
    } catch (error) {
      const apiError = handleApiError(error);
      return {
        success: false,
        data: null,
        message: apiError.message,
      };
    }
  },

  deleteProduct: async (orderId, productName) => {
    console.log("[deleteProduct] ", productName);
    try {
      const response = await axios.delete(`${BASE_URL}/${orderId}/products`, {
        data: { productName }, // Enviamos el nombre en el body
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteOrder: async (orderId) => {
    try {
      const response = await axios.delete(`${BASE_URL}/${orderId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Función auxiliar para formatear los datos del pedido
  formatOrderData: (
    orderItems,
    selectedTable,
    userId = null,
    isArqueoActive = false
  ) => {
    const total = orderItems.reduce(
      (sum, item) => sum + item.product.price * item.quantity,
      0
    );

    // Asegurar que isArqueoActive sea un booleano
    const arqueoActiveValue = isArqueoActive === true;

    console.log(
      "formatOrderData - Valor de isArqueoActive recibido:",
      isArqueoActive
    );
    console.log(
      "formatOrderData - Tipo de isArqueoActive recibido:",
      typeof isArqueoActive
    );
    console.log(
      "formatOrderData - Valor de isArqueoActive normalizado:",
      arqueoActiveValue
    );

    return {
      userId,
      mesaId: selectedTable.id,
      descriptionTable: selectedTable.description,
      items: orderItems.map((item) => ({
        productId: item.product.id,
        name: item.product.name,
        price: item.product.price,
        quantity: item.quantity,
        comments: item.comment || "",
        destination: item.product.destination,
        selectedAddon: item.selectedAddon,
      })),
      amount: total,
      status: "En Preparacion",
      date: new Date(),
      payment: "",
      preferencies: "",
      isArqueoActive: arqueoActiveValue,
    };
  },
};
